import React from 'react'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import style from './style'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CameraList from './CameraList'
import ManufacturerList from './ManufacturerList'
import getCurrentCamera from 'helpers/cameras/getCurrentCamera'
import { RECENT_DISPLAY_COUNT } from 'helpers/globals'
import Fuse from 'fuse.js'
import sortBy from 'ramda/src/sortBy'
import uniq from 'ramda/src/uniq'
import isEmpty from 'ramda/src/isEmpty'
import complement from 'ramda/src/complement'
import pipe from 'ramda/src/pipe'
import prop from 'ramda/src/prop'
import map from 'ramda/src/map'
import props from 'ramda/src/props'
import flatten from 'ramda/src/flatten'
import reject from 'ramda/src/reject'
import isNil from 'ramda/src/isNil'
import { recentModelsSelector } from 'selectors/components'

const mapStateToProps = state => {
  const { cameras, cameraModels, favoriteModels = [] } = state
  const currentCamera = getCurrentCamera(cameras)
  const model = currentCamera && currentCamera.model

  return {
    cameraModels,
    favoriteModels,
    recentModels: recentModelsSelector(state),
    currentModel: model && cameraModels.find(m => m.id === model.id),
    currentCamera
  }
}

class ModelSelector extends React.Component {
  constructor(props) {
    super(props)
    const { currentModel, currentCamera, cameraModels, favoriteModels, recentModels } = this.props
    this.normalizedCameras = this.normalizeCameras(cameraModels)
    this.manufacturers = uniq(this.normalizedCameras.map(c => c.manufacturer))

    const options = {
      threshold: 0.22,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      shouldSort: true,
      keys: [{
        name: 'trimmedModel',
        weight: 0.9
      }, {
        name: 'trimmedText',
        weight: 0.1
      }]
    }
    this.fuse = new Fuse(this.normalizedCameras, options)

    const filteredCameras = this.normalizedCameras.filter(c => recentModels.includes(c.id))

    const initialCamera = currentModel ? this.normalizeCameras([currentModel])[0] : null

    this.state = {
      selectedManufacturer: null,
      initialManufacturer: initialCamera && initialCamera.manufacturer,
      manufacturers: this.manufacturers,
      selectedCamera: null,
      initialCamera,
      initialAoV: currentModel ? currentModel.aovs.find(a => a.id === currentCamera.model.aov.id) : null,
      search: '',
      cameras: sortBy(c => recentModels.indexOf(c.id) === -1 ? RECENT_DISPLAY_COUNT + 1 : recentModels.indexOf(c.id))(filteredCameras),
      favoriteModels: favoriteModels || [],
      recentModels: recentModels,
      formFactors: [],
      category: 'recents'
    }
  }

  componentWillReceiveProps({ favoriteModels, recentModels }) {
    this.setState({
      favoriteModels,
      recentModels
    })
  }

  normalizeCameras = cameras => {
    return cameras.map(c => {
      return {
        ...c,
        totalPixels: c.resolution.split(/x/i)[0] * c.resolution.split(/x/i)[1],
        aovRange: isEmpty(c.aovs) ? [] : this.gatherAovs(c),
        trimmedText: c.text.replace(/\s|\\|\)|\(|\[|-/g, ''),
        trimmedModel: c.model.replace(/\s|\\|\)|\(|\[|-/g, '')
      }
    })
  }

  gatherAovs =
    pipe(
      prop('aovs'),
      map(props(['min', 'max'])),
      flatten,
      reject(isNil),
      map(parseFloat)
    )

  filterCameras = (formFactorChanges, searchText = null, selectedManufacturer, unselectManufacturer = false) => {
    const selectedManuf = selectedManufacturer || this.state.selectedManufacturer
    const search = searchText === null ? this.state.search : searchText
    const sanitizedSearch = search.replace(/\s|\\|\)|\(|\[|-/g, '')
    const formFactors = formFactorChanges || this.state.formFactors

    let cameras = (isEmpty(sanitizedSearch) ? this.normalizedCameras : this.fuse.search(sanitizedSearch))

    if (selectedManuf && !unselectManufacturer) {
      cameras = cameras.filter(c => c.manufacturer === selectedManuf)
    }

    if (complement(isEmpty)(formFactors)) {
      cameras = cameras.filter(c => formFactors.includes((c.form_factor || '').toLowerCase()))
    }

    this.setState({
      search,
      formFactors,
      cameras,
      selectedCamera: null,
      selectedManufacturer: unselectManufacturer ? null : selectedManuf,
      manufacturers: uniq(cameras.map(c => c.manufacturer))
    })
  }

  changeCategory = (category) => {
    if (category === this.state.category) return

    if (category === 'recents') {
      this.setState({ cameras: this.normalizedCameras.filter(c => this.state.recentModels.includes(c.id)), category, selectedManufacturer: null, selectedCamera: null })
    } else if (category === 'favorites') {
      this.setState({ cameras: this.normalizedCameras.filter(c => this.state.favoriteModels.includes(c.id)), category, selectedManufacturer: null, selectedCamera: null })
    } else {
      this.setState({ cameras: this.normalizedCameras, manufacturers: this.manufacturers, category, selectedManufacturer: null, selectedCamera: null })
    }
  }

  updateCameraList = selectedCamera =>
    this.setState({ selectedCamera })

  render() {
    const { changeCategory, updateCameraList, filterCameras, props: { close, classes }, state: { category, cameras, selectedCamera, initialCamera,
      initialManufacturer, initialAoV, favoriteModels, recentModels, manufacturers, selectedManufacturer, search, formFactors } } = this

    return (
      <div className={classes.modelSelector}>
        {
          category === 'all'
          ? <ManufacturerList {...{ cameras, selectedCamera, initialCamera, initialManufacturer, initialAoV, updateCameraList, favoriteModels,
              recentModels, manufacturers, selectedManufacturer, search, filterCameras, formFactors, close }} />
          : <CameraList {...{ cameras, selectedCamera, initialCamera, initialAoV, updateCameraList, favoriteModels, recentModels, category, close }} />
        }
        { !selectedCamera &&
          <div className={classes.bottomWrapper}>
            <div onClick={_ => changeCategory('recents')} className={`${classes.bottomButton} ${category === 'recents' && classes.selected}`}>
              <FontAwesomeIcon icon='history' className={classes.bottomIcon} />
              <p>Recents</p>
            </div>
            <div onClick={_ => changeCategory('favorites')} className={`${classes.bottomButton} ${category === 'favorites' && classes.selected}`}>
              <FontAwesomeIcon icon='star' className={classes.bottomIcon} />
              <p>Favorites</p>
            </div>
            <div onClick={_ => changeCategory('all')} className={`${classes.bottomButton} ${category === 'all' && classes.selected}`}>
              <FontAwesomeIcon icon='list-ul' className={classes.bottomIcon} />
              <p>All Models</p>
            </div>
          </div>
        }
      </div>
    )
  }
}

export default connect(mapStateToProps)(injectSheet(style)(ModelSelector))



// WEBPACK FOOTER //
// ./src/components/BottomBar/ModelSelector/index.js